import { formatLocalDateTime, formatRelativeDayTime } from '@shared/vueltoDateTime';
import { formatMoney } from '../../shared/format/public-api';
import { displayPaymentMethodLabel } from '../infra/salesPaymentLabels';
import type { SaleListRow } from '../domain/salesTypes';

/** Timestamp de venta para listas y detalle. */
export function formatSaleCreatedAt(iso: string | null | undefined): string {
  return formatLocalDateTime(iso);
}

/** Timestamp compacto para filas del listado: `Hoy 08:20` / `Ayer 14:05` / `24 sept 08:20`. */
export function formatSaleListTimestamp(iso: string | null | undefined): string {
  return formatRelativeDayTime(iso);
}

/**
 * Moneda de filas de ventas: enteros sin decimales ($950), fraccionales con 2
 * decimales fijos ($3.385,80). `toLocaleString` pelado dropea el cero final.
 */
export function formatSaleMoney(amount: number): string {
  const n = Number(amount);
  return formatMoney(Number.isFinite(n) ? n : 0);
}

const SALE_SHORT_REF_LEN = 6;
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Sufijo corto canónico del id de venta (6 chars finales, sin guiones). */
export function formatSaleShortRefSuffix(saleId: string | null | undefined): string {
  const raw = saleId?.trim() ?? '';
  if (!raw) return '';
  const compact = raw.replace(/-/g, '');
  const source = compact.length >= SALE_SHORT_REF_LEN ? compact : raw;
  return source.slice(-SALE_SHORT_REF_LEN).toLowerCase();
}

/** Etiqueta para listados: #47 (counter) o #a1b2c3 (legacy UUID suffix). */
export function formatSaleDisplayRef(params: {
  saleId?: string | null;
  counterNumber?: number | null;
}): string {
  const counter = params.counterNumber;
  if (counter != null && counter > 0) return `#${counter}`;
  return formatSaleShortRefLabel(params.saleId);
}

/** Etiqueta para listados: #a1b2c3 */
export function formatSaleShortRefLabel(saleId: string | null | undefined): string {
  const suffix = formatSaleShortRefSuffix(saleId);
  return suffix ? `#${suffix}` : '—';
}

/**
 * Etiqueta principal de referencia en ticket: `Pedido #47` (handoff) o
 * `Venta #47` (mostrador retail). `variant` default 'pedido' preserva callers.
 */
export function formatSaleOrderRefLabel(params: {
  saleId?: string | null;
  counterNumber?: number | null;
  variant?: 'pedido' | 'venta';
}): string | null {
  const counter = params.counterNumber;
  if (counter != null && counter > 0) {
    return params.variant === 'venta' ? `Venta #${counter}` : `Pedido #${counter}`;
  }
  return null;
}

/** Etiqueta de fulfillment para ticket / KDS. */
export function formatSaleFulfillmentLabel(type: string | null | undefined): string | null {
  if (type === 'pickup') return 'Para llevar';
  if (type === 'dine_in') return 'Comer acá';
  if (type === 'delivery') return 'Delivery';
  return null;
}

/**
 * Pie de ticket handoff cuando hay counter diario; null → copy retail genérica
 * (`Gracias por tu compra`). Sin `isPedido` o con `delivery` no hay pie de
 * mostrador: delivery no retira nada y retail no espera llamado.
 */
export function formatSaleReceiptFooter(params: {
  counterNumber?: number | null;
  fulfillmentType?: string | null;
  isPedido?: boolean;
}): string | null {
  const n = params.counterNumber;
  if (n == null || n <= 0) return null;
  if (params.isPedido === false) return null;
  if (params.fulfillmentType === 'dine_in') {
    return `Pedido #${n} — te avisamos cuando esté listo`;
  }
  if (params.fulfillmentType === 'delivery') return null;
  return `Pedido #${n} — retirá en mostrador cuando te llamemos`;
}

/** Nota de comanda cocina / KDS: `Pedido #47` o fallback `Venta #a1b2c3`. */
export function formatSaleKitchenNotes(
  saleId: string | null | undefined,
  counterNumber?: number | null,
  pickupName?: string | null,
): string {
  const ref =
    counterNumber != null && counterNumber > 0
      ? `Pedido #${counterNumber}`
      : (() => {
          const label = formatSaleShortRefLabel(saleId);
          return label === '—' ? 'Venta' : `Venta ${label}`;
        })();
  const name = pickupName?.trim();
  return name ? `${ref}\nRetiro: ${name}` : ref;
}

/** Texto principal de la fila de ventas (productos, no UUID). */
function formatSaleListPrimaryLabel(label: string): string {
  const trimmed = label.trim();
  if (!trimmed) return trimmed;
  if (UUID_RE.test(trimmed)) {
    return formatSaleShortRefLabel(trimmed);
  }
  if (trimmed.length > 48) return `${trimmed.slice(0, 45)}…`;
  return trimmed;
}

export function formatSaleListItemsSummary(row: { itemCount: number; firstItemLabel: string | null }): string {
  const n = row.itemCount;
  const first = row.firstItemLabel?.trim();
  if (n <= 0) return 'Sin ítems';
  if (!first) return n === 1 ? '1 ítem' : `${n} ítems`;
  const primary = formatSaleListPrimaryLabel(first);
  if (n === 1) return primary;
  return `${primary} · +${n - 1} más`;
}

export function spanishPaymentStatusLabel(status: string): string {
  if (status === 'paid' || status === 'approved') return 'Pagado';
  if (status === 'confirmed') return 'Confirmado';
  if (status === 'partial') return 'Parcial';
  if (status === 'pending') return 'Pago pendiente';
  if (status === 'created') return 'Cobro iniciado';
  if (status === 'expired') return 'QR vencido';
  if (status === 'fiado') return 'A cuenta';
  if (status === 'cancelled') return 'Anulado';
  return status;
}

function isMercadoPagoQrSale(paymentMethodIds: string[]): boolean {
  return paymentMethodIds.includes('mercadopago_qr');
}

/** Estado del comprobante electrónico (solo ventas fiscales). */
export function spanishFiscalInvoiceStatusLabel(status: string): string {
  if (status === 'pending_sync' || status === 'pending') return 'Factura pendiente';
  if (status === 'approved' || status === 'synced' || status === 'ok') return 'Factura emitida';
  if (status === 'pending_retry') return 'Reintento pendiente';
  if (status === 'error') return 'Error en factura';
  if (status === 'not_required') return 'Sin comprobante';
  return status;
}

/** Prioriza invoice aprobado/CAE sobre fiscal_status desactualizado en sales. */
export function resolveFiscalInvoiceDisplayStatus(params: {
  fiscalStatus: string;
  fiscalMode?: string | null;
  invoiceStatus?: string | null;
  invoiceCae?: string | null;
}): string {
  if (params.fiscalMode === 'non_fiscal') return 'No fiscal';
  const inv = params.invoiceStatus?.trim();
  if (inv === 'approved' || params.invoiceCae?.trim()) {
    return spanishFiscalInvoiceStatusLabel('approved');
  }
  if (inv === 'pending_retry') return spanishFiscalInvoiceStatusLabel('pending_retry');
  if (inv === 'error') return spanishFiscalInvoiceStatusLabel('error');
  if (inv === 'pending') return spanishFiscalInvoiceStatusLabel('pending_sync');
  return spanishFiscalInvoiceStatusLabel(params.fiscalStatus);
}

export type SaleListStatusChipTone = 'success' | 'warning' | 'danger' | 'neutral' | 'service';

export type SaleListStatusChip = {
  key: string;
  label: string;
  tone: SaleListStatusChipTone;
};

function paymentStatusChipTone(status: string): SaleListStatusChipTone {
  if (status === 'paid' || status === 'approved' || status === 'confirmed') return 'success';
  if (status === 'cancelled' || status === 'expired') return 'danger';
  if (status === 'partial' || status === 'pending' || status === 'fiado' || status === 'created') return 'warning';
  return 'neutral';
}

/** Canal de venta para UI (listado, detalle, CSV). Online se etiqueta como Pedido online. */
export function formatSaleChannelLabel(channel: string | null | undefined): string {
  return channel === 'online' ? 'Pedido online' : 'Mostrador';
}

export function isOnlineSaleChannel(channel: string | null | undefined): boolean {
  return channel === 'online';
}

export type SalePaymentStatusPresentation = {
  label: string;
  tone: SaleListStatusChipTone;
};

export function resolveSalePaymentStatusPresentation(
  paymentStatus: string,
  paymentMethodIds: string[] = []
): SalePaymentStatusPresentation {
  if (
    isMercadoPagoQrSale(paymentMethodIds) &&
    paymentStatus !== 'paid' &&
    paymentStatus !== 'approved' &&
    paymentStatus !== 'cancelled'
  ) {
    return { label: 'QR Mercado Pago', tone: 'service' };
  }
  return {
    label: spanishPaymentStatusLabel(paymentStatus),
    tone: paymentStatusChipTone(paymentStatus),
  };
}

/** Chip único para grilla mínima del libro de ventas (anulada / pago pendiente). */
export function buildSaleListMinimalStatusChip(params: {
  status: string;
  paymentStatus: string;
}): SaleListStatusChip | null {
  if (params.status === 'voided') {
    return { key: 'voided', label: 'Anulada', tone: 'danger' };
  }
  if (params.paymentStatus === 'paid' || params.paymentStatus === 'approved') {
    return null;
  }
  if (params.paymentStatus === 'partial') {
    return { key: 'partial', label: 'Parcial', tone: 'warning' };
  }
  return { key: 'pending', label: 'Pago pendiente', tone: 'warning' };
}

export function resolveSalePaymentLinePresentation(
  status: string,
  methodId?: string
): SalePaymentStatusPresentation {
  if (methodId === 'fiado' && status === 'confirmed') {
    return { label: 'Pendiente', tone: 'warning' };
  }
  const label = spanishSalePaymentLineStatus(status);
  const toneStatus = status === 'confirmed' || status === 'approved' ? 'paid' : status;
  return {
    label,
    tone: paymentStatusChipTone(toneStatus),
  };
}

function fiscalInvoiceChipTone(params: {
  fiscalStatus: string;
  invoiceStatus?: string | null;
  invoiceCae?: string | null;
}): SaleListStatusChipTone {
  const inv = params.invoiceStatus?.trim();
  if (inv === 'approved' || params.invoiceCae?.trim()) return 'success';
  if (inv === 'error' || params.fiscalStatus === 'error') return 'danger';
  if (
    inv === 'pending_retry' ||
    params.fiscalStatus === 'pending_retry' ||
    inv === 'pending' ||
    params.fiscalStatus === 'pending_sync'
  ) {
    return 'warning';
  }
  return 'neutral';
}

/** Chips compactos para filas del libro de ventas y encabezado del detalle. */
export function buildSaleStatusChips(params: {
  paymentStatus: string;
  fiscalMode: string;
  fiscalStatus: string;
  synced: boolean;
  isVoided: boolean;
  paymentMethodIds?: string[];
  invoiceStatus?: string | null;
  invoiceCae?: string | null;
  includeMpQrChip?: boolean;
}): SaleListStatusChip[] {
  if (params.isVoided) {
    return [{ key: 'voided', label: 'Anulada', tone: 'danger' }];
  }

  const mpQrSale = isMercadoPagoQrSale(params.paymentMethodIds ?? []);
  const chips: SaleListStatusChip[] = [];

  if (mpQrSale && params.includeMpQrChip) {
    chips.push({ key: 'mp_qr', label: 'QR Mercado Pago', tone: 'service' });
  } else if (!mpQrSale) {
    chips.push({
      key: 'payment',
      label: spanishPaymentStatusLabel(params.paymentStatus),
      tone: paymentStatusChipTone(params.paymentStatus),
    });
  }

  if (params.fiscalMode !== 'non_fiscal') {
    chips.push({
      key: 'invoice',
      label: resolveFiscalInvoiceDisplayStatus({
        fiscalStatus: params.fiscalStatus,
        invoiceStatus: params.invoiceStatus,
        invoiceCae: params.invoiceCae,
      }),
      tone: fiscalInvoiceChipTone(params),
    });
  } else {
    chips.push({ key: 'mode', label: 'No fiscal', tone: 'neutral' });
  }

  // ADR-017 online-only: no chip de sync device (listado remoto siempre synced).
  return chips;
}

const PAID_PAYMENT_STATUSES = new Set(['paid', 'approved', 'confirmed']);

export type SaleListActionablePill = { label: string; tone: 'bad' | 'warn' };

/**
 * Pill de fila solo cuando hay algo que inspeccionar (proto ventas-ux-v1:
 * "sin pill en ventas sanas"). Anulada → roja; cobro pendiente / QR sin
 * cobrar / factura en excepción → amarilla. Venta paga y en regla → null.
 */
export function resolveSaleListActionablePill(row: SaleListRow): SaleListActionablePill | null {
  const isVoided = row.status === 'voided';
  const chips = buildSaleStatusChips({
    paymentStatus: row.paymentStatus,
    fiscalMode: row.fiscalMode,
    fiscalStatus: row.fiscalStatus,
    synced: row.synced,
    isVoided,
    paymentMethodIds: row.paymentMethodIds,
    invoiceStatus: row.invoiceStatus,
    invoiceCae: row.invoiceCae,
    includeMpQrChip: true,
  });
  if (isVoided) return { label: 'Anulada', tone: 'bad' };

  const paid = PAID_PAYMENT_STATUSES.has(row.paymentStatus);
  for (const chip of chips) {
    if (chip.key === 'mp_qr') {
      if (!paid) return { label: 'QR sin cobrar', tone: 'warn' };
      continue;
    }
    if (chip.tone === 'danger') return { label: chip.label, tone: 'bad' };
    if (chip.tone === 'warning') return { label: chip.label, tone: 'warn' };
  }
  return null;
}

export function spanishSaleStatusLabel(status: string | null | undefined): string {
  if (status === 'voided') return 'Anulada';
  if (status === 'active' || !status) return 'Activa';
  // Nunca mostrar keys en inglés crudas en UI.
  return 'Activa';
}

export function formatSaleListPaymentMethods(
  methodIds: string[],
  paymentMethodNames: Record<string, string>
): string {
  if (methodIds.length === 0) return 'Sin medio de cobro';
  const labels = methodIds.map((id) => displayPaymentMethodLabel(id, paymentMethodNames));
  return labels.join(' + ');
}

/** Estados fiscales "felices" que no aportan en la fila — solo se listan excepciones. */
const FISCAL_QUIET_LABELS = new Set(['No fiscal', 'Factura emitida', 'Sin comprobante', '—']);

/**
 * Meta de la fila de ventas (listado desktop y mobile), deduplicada:
 * - `A cuenta` y `Cliente` no se repiten (fiado gana, es el dato accionable).
 * - `Operador` se omite cuando la venta es del usuario actual.
 * - `Fiscal` solo aparece en excepciones (pendiente / reintento / error).
 */
export function buildSaleListMeta(params: {
  row: SaleListRow;
  paymentMethodNames: Record<string, string>;
  diningTableName?: string | null;
  operatorLabel?: string | null;
  selfUserId?: string | null;
}): string {
  const { row, paymentMethodNames } = params;
  const parts: (string | null)[] = [
    formatSaleListItemsSummary(row),
    formatSaleListPaymentMethods(row.paymentMethodIds, paymentMethodNames),
    params.diningTableName ? `Mesa: ${params.diningTableName}` : null,
    row.fiadoCustomerName
      ? `A cuenta: ${row.fiadoCustomerName}`
      : row.customerName
        ? `Cliente: ${row.customerName}`
        : null,
    params.operatorLabel && row.cashierUserId !== params.selfUserId
      ? `Operador: ${params.operatorLabel}`
      : null,
  ];
  const fiscalLabel = resolveFiscalInvoiceDisplayStatus({
    fiscalStatus: row.fiscalStatus,
    fiscalMode: row.fiscalMode,
    invoiceStatus: row.invoiceStatus,
    invoiceCae: row.invoiceCae,
  });
  if (!FISCAL_QUIET_LABELS.has(fiscalLabel)) {
    parts.push(`Fiscal: ${fiscalLabel}`);
  }
  return parts.filter(Boolean).join(' · ');
}

export function formatSalePendingLine(pendingAmount: number, paymentStatus: string): string | null {
  if (pendingAmount <= 0) return null;
  const money = formatSaleMoney(pendingAmount);
  if (paymentStatus === 'fiado' || paymentStatus === 'partial') {
    return `Pendiente ${money}`;
  }
  if (paymentStatus === 'pending') {
    return `Cobro pendiente ${money}`;
  }
  return `Saldo ${money}`;
}

export function formatMercadoPagoPaymentDetail(params: {
  reference?: string | null;
  metadata?: Record<string, unknown> | null;
}): { operationNumber: string | null; orderNumber: string | null; internalRef: string | null } {
  const meta = params.metadata ?? {};
  const mpOperationNumber =
    (typeof meta.mpOperationNumber === 'string' && meta.mpOperationNumber.trim()) ||
    (typeof meta.reference_id === 'string' && /^\d+$/.test(meta.reference_id.trim())
      ? meta.reference_id.trim()
      : null);
  const orderId =
    (typeof meta.orderId === 'string' && meta.orderId.trim()) ||
    (typeof meta.order_id === 'string' && meta.order_id.trim()) ||
    null;
  const internalRef = params.reference?.trim() || null;
  return {
    operationNumber: mpOperationNumber,
    orderNumber: orderId,
    internalRef,
  };
}

export function formatInvoiceCaeLine(cae: string | null | undefined, issuedAt: string | null | undefined): string | null {
  if (!cae?.trim()) return null;
  const datePart = issuedAt ? formatSaleCreatedAt(issuedAt) : null;
  return datePart ? `CAE ${cae.trim()} · ${datePart}` : `CAE ${cae.trim()}`;
}

export function formatCashSessionLabel(sessionId: string | null | undefined): string | null {
  if (!sessionId?.trim()) return null;
  const id = sessionId.trim();
  if (id.length <= 14) return `Sesión ${id}`;
  return `Sesión ${id.slice(0, 8)}…${id.slice(-4)}`;
}

function spanishSalePaymentLineStatus(status: string): string {
  if (status === 'confirmed') return 'Confirmado';
  if (status === 'pending') return 'Pendiente';
  if (status === 'created') return 'Iniciado';
  if (status === 'expired') return 'Vencido';
  if (status === 'approved') return 'Aprobado';
  if (status === 'failed') return 'Fallido';
  if (status === 'cancelled') return 'Anulado';
  return status;
}
